import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mountain, Camera, MapPin, AlertTriangle, Wind, Droplets, Crown } from 'lucide-react';
import './GuidePage.css';

export default function HighlandsGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🏔️ Highlands - Schottlands Herzstück</h1>
        
        <div className="info-box">
          <p>
            Die Highlands sind DAS Schottland-Erlebnis: Endlose Weiten, dramatische Berge, 
            einsame Glens (Täler) und der legendäre <strong>NC500</strong> (North Coast 500). 
            Hier findest du die anspruchsvollsten und schönsten Radstrecken Schottlands!
          </p>
        </div>

        <section>
          <h2>Übersicht Highlands</h2>
          
          <div className="data-grid">
            <div className="data-item">
              <h3>📍 Geografie</h3>
              <p>Nördlich der Highland Boundary Fault</p>
              <p>~26.000 km² (1/3 von Schottland)</p>
            </div>
            <div className="data-item">
              <h3>👥 Bevölkerung</h3>
              <p>Nur ~230.000 Einwohner</p>
              <p>Extrem dünn besiedelt</p>
            </div>
            <div className="data-item">
              <h3>🚴 Charakter</h3>
              <p>Bergig, einsam, ursprünglich</p>
              <p>Single-track roads häufig</p>
            </div>
            <div className="data-item">
              <h3>⭐ Schwierigkeit</h3>
              <p>Mittel bis Sehr Schwer</p>
              <p>Höhenmeter & Wind!</p>
            </div>
          </div>

          <h3>Hauptregionen der Highlands</h3>
          <ul>
            <li><strong>Western Highlands:</strong> Fort William, Glen Coe, Loch Ness</li>
            <li><strong>Northern Highlands:</strong> NC500-Gebiet, Sutherland, Caithness</li>
            <li><strong>Eastern Highlands:</strong> Cairngorms, Speyside</li>
            <li><strong>Central Highlands:</strong> Grampian Mountains, Perthshire</li>
          </ul>
        </section>

        <section>
          <h2>1. North Coast 500 (NC500) - Die Kult-Route</h2>
          
          <div className="warning-box">
            <Crown size={20} />
            <strong>NC500 = Schottlands Route 66!</strong> Die legendäre 830km Rundstrecke 
            durch die nördlichen Highlands. Spektakulär, aber extrem anspruchsvoll!
          </div>

          <h3>Route-Übersicht</h3>
          <div className="info-box">
            <ul>
              <li><strong>Start/Ziel:</strong> Inverness</li>
              <li><strong>Distanz:</strong> 830 km (515 Meilen)</li>
              <li><strong>Höhenmeter:</strong> ~9.000m kumulative Anstiege</li>
              <li><strong>Dauer:</strong> 7-12 Tage (je nach Fitness)</li>
              <li><strong>Richtung:</strong> Uhrzeigersinn empfohlen (Westküste zuerst)</li>
              <li><strong>Beste Zeit:</strong> Mai - September</li>
            </ul>
          </div>

          <h3>NC500 Etappen-Plan (10 Tage)</h3>
          
          <h4>Tag 1: Inverness - Ullapool (95km, 1200m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A835 via Garve</li>
            <li><strong>Highlights:</strong> Corrieshalloch Gorge, Loch Broom</li>
            <li><strong>Schwierigkeit:</strong> Mittel (wellige Strecke)</li>
          </ul>

          <h4>Tag 2: Ullapool - Lochinver (60km, 800m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A835 + A837 (Westküste beginnt!)</li>
            <li><strong>Highlights:</strong> Stac Pollaidh, Assynt Mountains</li>
            <li><strong>Schwierigkeit:</strong> Mittel (Anstiege mit Aussicht)</li>
          </ul>

          <h4>Tag 3: Lochinver - Scourie (50km, 600m HM)</h4>
          <ul>
            <li><strong>Route:</strong> B869 (spektakuläre Küstenstraße!)</li>
            <li><strong>Highlights:</strong> Drumbeg Loop, wilde Küste</li>
            <li><strong>Schwierigkeit:</strong> Schwer (Single-track, wellig)</li>
          </ul>

          <h4>Tag 4: Scourie - Durness (60km, 700m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A838 Nordwestküste</li>
            <li><strong>Highlights:</strong> Kylesku Bridge, Loch Glendhu</li>
            <li><strong>Schwierigkeit:</strong> Mittel</li>
          </ul>

          <h4>Tag 5: Durness - Tongue (50km, 600m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A838 Nordküste</li>
            <li><strong>Highlights:</strong> Cape Wrath (optional mit Fähre), Smoo Cave</li>
            <li><strong>Schwierigkeit:</strong> Leicht (flacher)</li>
          </ul>

          <h4>Tag 6: Tongue - Thurso (80km, 800m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A836 via Bettyhill</li>
            <li><strong>Highlights:</strong> Strathy Point, Nordküsten-Strände</li>
            <li><strong>Schwierigkeit:</strong> Mittel (Wind!)</li>
          </ul>

          <h4>Tag 7: Thurso - John o' Groats - Helmsdale (120km, 1000m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A836 + A99 + A9 (lange Etappe!)</li>
            <li><strong>Highlights:</strong> John o' Groats (nördlichster Punkt!), Duncansby Stacks</li>
            <li><strong>Schwierigkeit:</strong> Schwer (Distanz + Berufsverkehr A9)</li>
            <li><strong>Alternative:</strong> In 2 Tage teilen (Übernachtung Wick)</li>
          </ul>

          <h4>Tag 8: Helmsdale - Dornoch (70km, 900m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A9 Ostküste</li>
            <li><strong>Highlights:</strong> Dunrobin Castle, Golspie</li>
            <li><strong>Schwierigkeit:</strong> Mittel (Verkehr auf A9)</li>
          </ul>

          <h4>Tag 9: Dornoch - Inverness via Black Isle (100km, 800m HM)</h4>
          <ul>
            <li><strong>Route:</strong> A9 + Black Isle Loop (optional)</li>
            <li><strong>Highlights:</strong> Cromarty, Dolphins (Chanonry Point)</li>
            <li><strong>Schwierigkeit:</strong> Leicht-Mittel</li>
          </ul>

          <h4>Tag 10: Puffertag / Rest Day</h4>
          <ul>
            <li>Inverness erkunden oder NC500 abschließen</li>
          </ul>

          <div className="pro-tip">
            <h3>💡 NC500 Pro-Tipps</h3>
            <ul>
              <li><strong>Uhrzeigersinn fahren:</strong> Westküste zuerst (beste Aussicht, Verkehr auf linker Seite)</li>
              <li><strong>Früh starten:</strong> Viele Touristen ab 10 Uhr (Wohnmobile!)</li>
              <li><strong>Unterkünfte VORBUCHEN:</strong> NC500 ist extrem populär (Mai-Sept ausgebucht)</li>
              <li><strong>Lebensmittel mitnehmen:</strong> Shops selten nach Ullapool</li>
              <li><strong>Single-Track Etiquette:</strong> Radfahrer sollten Autos vorbeilassen</li>
              <li><strong>Wind von Westen:</strong> Westküste oft Gegenwind (einplanen!)</li>
              <li><strong>E-Bike empfohlen:</strong> 9.000 Höhenmeter sind brutal ohne Motor</li>
              <li><strong>Midges-Schutz:</strong> Ab Juni extrem! Netz & Spray Pflicht</li>
            </ul>
          </div>

          <h3>NC500 Varianten</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>Schnelle Tour (7 Tage):</strong> 120km/Tag, nur Hauptstraßen
            </div>
            <div className="data-item">
              <strong>Standard Tour (10 Tage):</strong> 80-90km/Tag, empfohlen
            </div>
            <div className="data-item">
              <strong>Entspannte Tour (14 Tage):</strong> 60km/Tag, Zeit für Sehenswürdigkeiten
            </div>
            <div className="data-item">
              <strong>Extended NC500 (16+ Tage):</strong> Mit Applecross, Cape Wrath, Detours
            </div>
          </div>
        </section>

        <section>
          <h2>2. Western Highlands - Dramatik pur</h2>
          
          <h3>Fort William - Outdoor-Hauptstadt</h3>
          <div className="info-box">
            <p>
              <strong>Fort William</strong> ist das Tor zu den Western Highlands und Ausgangspunkt 
              für viele legendäre Routen. Hier findest du Ben Nevis (höchster Berg UK), Glen Coe 
              und die Road to the Isles.
            </p>
          </div>

          <h4>Top-Routen Western Highlands</h4>

          <h4>1. Glen Coe Loop (40km, 600m HM)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Route:</strong> A82 durch Glen Coe</li>
              <li><strong>Highlights:</strong> Drei Schwestern, Buachaille Etive Mor</li>
              <li><strong>Achtung:</strong> Viel Verkehr auf A82 (Tour Buses!)</li>
              <li><strong>Tipp:</strong> Früh morgens fahren (vor 9 Uhr)</li>
            </ul>
          </div>

          <h4>2. Road to the Isles (Fort William - Mallaig, 70km, 600m HM)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Route:</strong> A830 entlang Loch Eil & Loch nan Uamh</li>
              <li><strong>Highlights:</strong> Glenfinnan Viaduct (Harry Potter!), Arisaig Beaches</li>
              <li><strong>Ziel:</strong> Mallaig (Fähre nach Skye möglich)</li>
              <li><strong>Schwierigkeit:</strong> Mittel (wellig, aber machbar)</li>
            </ul>
          </div>

          <h4>3. Great Glen Way (Fort William - Inverness, 120km, 800m HM)</h4>
          <div className="info-box">
            <ul>
              <li><strong>Route:</strong> Caledonian Canal & Loch Ness (Mix aus Trail & Straße)</li>
              <li><strong>Highlights:</strong> Loch Ness, Neptune's Staircase (Schleusen)</li>
              <li><strong>Achtung:</strong> Teilweise Gravel/Trail (Gravelbike empfohlen)</li>
              <li><strong>Dauer:</strong> 2-3 Tage</li>
            </ul>
          </div>

          <h4>4. Applecross Pass (Bealach na Bà) - Die Legende!</h4>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>ACHTUNG: Eine der steilsten Straßen UK!</strong><br />
            Bealach na Bà = 626m Pass mit 20% Steigung!
          </div>
          <div className="info-box">
            <ul>
              <li><strong>Route:</strong> Von Tornapress über Bealach na Bà nach Applecross (25km, 800m HM)</li>
              <li><strong>Steigung:</strong> Bis zu 20% (!) auf 10km</li>
              <li><strong>Aussicht:</strong> Spektakulär! Skye, Raasay sichtbar</li>
              <li><strong>Rückweg:</strong> Küstenstraße (leichter, landschaftlich toll)</li>
              <li><strong>E-Bike:</strong> SEHR empfohlen (sonst schieben wahrscheinlich)</li>
            </ul>
          </div>

          <h3>Loch Ness - Die Touristen-Falle?</h3>
          <ul>
            <li><strong>A82 am Loch Ness:</strong> Viel Verkehr, wenig Aussicht (Bäume blockieren!)</li>
            <li><strong>B852/B862 Ostseite:</strong> Ruhiger, aber schmalere Straßen</li>
            <li><strong>Urquhart Castle:</strong> Sehenswert, aber überlaufen</li>
            <li><strong>Unser Tipp:</strong> Loch Ness überspringen, es gibt schönere Lochs!</li>
          </ul>
        </section>

        <section>
          <h2>3. Eastern Highlands - Cairngorms & Whisky</h2>
          
          <h3>Cairngorms National Park</h3>
          <div className="info-box">
            <p>
              Größter Nationalpark UK, Heimat von 4 der 5 höchsten Berge Schottlands. 
              <strong>Weniger touristisch als Westküste</strong>, aber landschaftlich spektakulär!
            </p>
          </div>

          <h4>Top-Routen Cairngorms</h4>

          <h4>1. Speyside Way (Aviemore - Craigellachie, 100km, 1200m HM)</h4>
          <ul>
            <li><strong>Route:</strong> Mix aus Trail & ruhigen Straßen</li>
            <li><strong>Highlights:</strong> Whisky-Destillerien (Glenlivet, Glenfiddich, Macallan)</li>
            <li><strong>Dauer:</strong> 2-3 Tage</li>
            <li><strong>Achtung:</strong> Teilweise Gravel-Abschnitte</li>
          </ul>

          <h4>2. Lecht Pass (A939) - Höchste öffentliche Straße</h4>
          <div className="info-box">
            <ul>
              <li><strong>Route:</strong> Ballater - Tomintoul (638m Pass!)</li>
              <li><strong>Distanz:</strong> 45km, 900m HM</li>
              <li><strong>Im Winter:</strong> Oft geschlossen (Schnee!)</li>
              <li><strong>Aussicht:</strong> Cairngorms Panorama</li>
            </ul>
          </div>

          <h3>Whisky Trail per Rad</h3>
          <div className="pro-tip">
            <h3>🥃 Malt Whisky Trail (Speyside)</h3>
            <p>
              Speyside = Whisky-Herz Schottlands! Über 50 Destillerien in der Region.
            </p>
            <ul>
              <li><strong>Route:</strong> Elgin - Dufftown - Aberlour - Craigellachie (Loop, 80km)</li>
              <li><strong>Destillerien:</strong> Glenfiddich, Macallan, Glenlivet, Aberlour, Balvenie</li>
              <li><strong>Tipp:</strong> Max. 2 Destillery-Touren/Tag (Verkostungen!)</li>
              <li><strong>Übernachtung:</strong> In Dufftown planen (Zentrum des Whisky-Dreiecks)</li>
              <li><strong>ACHTUNG:</strong> Nicht betrunken Rad fahren! (siehe Verkehrsregeln-Guide)</li>
            </ul>
          </div>

          <h3>Royal Deeside</h3>
          <ul>
            <li><strong>Route:</strong> Aberdeen - Braemar (A93, 100km)</li>
            <li><strong>Highlights:</strong> Balmoral Castle (Queen's Sommerresidenz)</li>
            <li><strong>Landschaft:</strong> Sanfter als Westküste, aber schön</li>
          </ul>
        </section>

        <section>
          <h2>4. Central Highlands - Perthshire</h2>
          
          <h3>Tor zu den Highlands</h3>
          <p>
            Perthshire liegt am Rand der Highlands und bietet einen sanfteren Einstieg 
            mit weniger extremem Terrain als NC500 oder Glen Coe.
          </p>

          <h4>Top-Routen Perthshire</h4>

          <h4>1. Pitlochry - Aberfeldy Loop (60km, 700m HM)</h4>
          <ul>
            <li>Schottisches Landschafts-Idyll</li>
            <li>Mehrere Whisky-Destillerien (Blair Athol, Dewar's)</li>
            <li>Perfekt für Anfänger-Highlander</li>
          </ul>

          <h4>2. Loch Rannoch Loop (80km, 600m HM)</h4>
          <ul>
            <li>Einsamer als Loch Ness</li>
            <li>Single-track road um den Loch</li>
            <li>Black Wood of Rannoch (alter Caledonian Forest)</li>
          </ul>
        </section>

        <section>
          <h2>5. Praktische Infos Highlands</h2>
          
          <h3>Infrastruktur & Versorgung</h3>
          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Highlands sind SEHR dünn besiedelt!</strong> Tankstellen, Shops und Cafés 
            sind selten. Plane voraus!
          </div>

          <h4>Versorgung auf NC500</h4>
          <ul>
            <li><strong>Inverness:</strong> Alles verfügbar (letzte große Stadt)</li>
            <li><strong>Ullapool:</strong> Gute Shops & Restaurants</li>
            <li><strong>Lochinver:</strong> Kleiner Shop (begrenzt)</li>
            <li><strong>Durness:</strong> Mini-Shop (Basics)</li>
            <li><strong>Thurso:</strong> Wieder größere Stadt</li>
            <li><strong>Zwischen Ullapool & Thurso:</strong> Fast nichts! Snacks mitnehmen</li>
          </ul>

          <h4>Unterkünfte</h4>
          <ul>
            <li><strong>Hotels/B&Bs:</strong> Begrenzt, NC500 = vorbuchen!</li>
            <li><strong>Hostels:</strong> Selten (Inverness, Fort William, Ullapool)</li>
            <li><strong>Wild Camping:</strong> Legal (Scottish Outdoor Access Code)</li>
            <li><strong>Campingplätze:</strong> Vorhanden, aber nicht überall</li>
          </ul>

          <h3>Wetter & Beste Reisezeit</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>Mai - Juni:</strong> Beste Zeit! Lange Tage, Midges noch OK, weniger Touristen
            </div>
            <div className="data-item">
              <strong>Juli - August:</strong> Wärmste, aber: Midges-Hölle + NC500 überfüllt
            </div>
            <div className="data-item">
              <strong>September:</strong> Ruhiger, Herbstfarben, mehr Regen, Midges weg
            </div>
            <div className="data-item">
              <strong>Oktober - April:</strong> Viele Unterkünfte geschlossen, Schnee möglich, stürmisch
            </div>
          </div>

          <h3>Wind & Regen</h3>
          <ul>
            <li><strong>Wind:</strong> Fast immer aus Westen (Gegenwind Westküste!)</li>
            <li><strong>Regen:</strong> Westküste = 2x mehr Regen als Ostküste</li>
            <li><strong>Wetter wechselt schnell:</strong> 4 Jahreszeiten pro Tag möglich</li>
            <li><strong>Tipp:</strong> Immer Regenkleidung griffbereit</li>
          </ul>

          <h3>Single-Track Roads</h3>
          <div className="info-box">
            <h4>🛣️ Single-Track Etiquette</h4>
            <ul>
              <li><strong>Passing Places:</strong> Kleine Buchten zum Ausweichen</li>
              <li><strong>Regel:</strong> Wer näher an Passing Place ist, fährt vor</li>
              <li><strong>Als Radfahrer:</strong> Immer rechts ranfahren, Autos vorbeilassen</li>
              <li><strong>Danken:</strong> Hand heben als Dank (Highland-Höflichkeit)</li>
              <li><strong>Nicht IN Passing Place warten:</strong> Davor oder dahinter!</li>
            </ul>
          </div>

          <h3>Midges - Der Horror der Highlands</h3>
          <div className="warning-box">
            <AlertTriangle size={24} />
            <strong>MIDGES SIND REAL!</strong><br />
            Winzige Stechmücken (1-2mm), die in Schwärmen angreifen. Juni-August = Hölle!
          </div>
          <ul>
            <li><strong>Was sind Midges?</strong> Winzige blutsaugende Mücken (keine Malaria)</li>
            <li><strong>Wann:</strong> Mai - September, Peak: Juli-August</li>
            <li><strong>Wo:</strong> Überall in Highlands, besonders Westküste & Seen</li>
            <li><strong>Aktivität:</strong> Morgens/Abends, bei Windstille, Schatten</li>
            <li><strong>Schutz:</strong>
              <ul>
                <li>Midge-Netz für Kopf (ABSOLUT NÖTIG!)</li>
                <li>Smidge-Spray (spezielles Midge-Repellent)</li>
                <li>Lange Kleidung auch bei Hitze</li>
                <li>Bei Wind fahren (Midges können bei >10km/h Wind nicht fliegen)</li>
                <li>Pausen in sonnigen, windigen Spots</li>
              </ul>
            </li>
          </ul>
        </section>

        <section>
          <h2>6. Wildlife & Natur</h2>
          
          <h3>Tiere der Highlands</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>🦌 Red Deer:</strong> Überall! Vorsicht beim Radfahren (können auf Straße springen)
            </div>
            <div className="data-item">
              <strong>🐏 Highland Cattle:</strong> Langhaarige Rinder (sehen gefährlich aus, sind aber friedlich)
            </div>
            <div className="data-item">
              <strong>🦅 Golden Eagle:</strong> Selten zu sehen (Cairngorms & Nordwesten)
            </div>
            <div className="data-item">
              <strong>🐑 Schafe:</strong> Überall! Vorsicht, laufen oft auf Straße
            </div>
          </div>

          <h3>Landschaft</h3>
          <ul>
            <li><strong>Glens:</strong> U-förmige Täler (von Gletschern geformt)</li>
            <li><strong>Lochs:</strong> Seen (meist Süßwasser, manche Salzwasser = Sea Lochs)</li>
            <li><strong>Munros:</strong> Berge über 914m (282 in Schottland, 177 in Highlands)</li>
            <li><strong>Bogs & Moorland:</strong> Torflandschaft (CO2-Speicher)</li>
          </ul>
        </section>

        <section>
          <h2>7. Sicherheit & Notfall</h2>
          
          <div className="warning-box">
            <AlertTriangle size={24} />
            <strong>Highlands = Wilderness!</strong> Mobilfunk oft nicht vorhanden, 
            nächstes Krankenhaus kann 100km entfernt sein!
          </div>

          <h3>Notfall-Vorbereitung</h3>
          <ul>
            <li><strong>Notfall-Nummer:</strong> 999 (funktioniert auch ohne Signal mit Emergency-Modus)</li>
            <li><strong>Erste Hilfe Set:</strong> Unbedingt mitnehmen</li>
            <li><strong>Spot/Garmin InReach:</strong> Satellitengerät für echte Wildnis</li>
            <li><strong>Jemanden informieren:</strong> Route & erwartete Ankunft mitteilen</li>
            <li><strong>Ersatzschlauch & Werkzeug:</strong> Nächster Bike-Shop kann 80km entfernt sein</li>
          </ul>

          <h3>Häufige Probleme</h3>
          <ul>
            <li><strong>Platten:</strong> Oft durch Cattle Grid Metall oder Scherben</li>
            <li><strong>Unterzuckerung:</strong> Lange Distanzen ohne Verpflegung</li>
            <li><strong>Unterkühlung:</strong> Auch im Sommer bei Regen + Wind möglich</li>
            <li><strong>Orientierung:</strong> Bei Nebel schwierig (GPS offline verfügbar haben!)</li>
          </ul>
        </section>

        <div className="pro-tip">
          <h3>🎯 Die 10 wichtigsten Highlands-Tipps</h3>
          <ol>
            <li><strong>Vorbuchen:</strong> Unterkünfte auf NC500 (Mai-Sept)</li>
            <li><strong>E-Bike erwägen:</strong> 9.000 Höhenmeter sind brutal</li>
            <li><strong>Midges-Schutz:</strong> Netz & Spray (nicht verhandelbar!)</li>
            <li><strong>Snacks mitnehmen:</strong> Versorgung oft 80km entfernt</li>
            <li><strong>Früh starten:</strong> Weniger Verkehr, besseres Licht</li>
            <li><strong>Single-Track Etiquette:</strong> Autos vorbeilassen, Hand heben</li>
            <li><strong>Regenkleidung bereit:</strong> Wetter wechselt in Minuten</li>
            <li><strong>Wind respektieren:</strong> Westwind = Gegenwind Westküste</li>
            <li><strong>Flexibel bleiben:</strong> Sturm-Tage im Pub verbringen</li>
            <li><strong>Genießen!</strong> Highlands = Einmalig, nimm dir Zeit</li>
          </ol>
        </div>

        <div className="info-box">
          <h4>📚 Weiterführende Guides</h4>
          <ul>
            <li><Link to="/wissen/ausruestung">🎒 Ausrüstung</Link> - Highlands-spezifische Packliste</li>
            <li><Link to="/wissen/fahrtechnik">🚴 Fahrtechnik</Link> - Anstiege & Wind meistern</li>
            <li><Link to="/wissen/sicherheit">🦺 Sicherheit</Link> - Notfallplanung</li>
            <li><Link to="/wissen/verkehrsregeln">🚦 Verkehrsregeln</Link> - Single-Track Roads</li>
          </ul>
        </div>

        <div className="download-section">
          <h3>📥 Downloads</h3>
          <button className="download-btn" onClick={() => window.print()}>
            Highlands-Guide als PDF speichern
          </button>
        </div>
      </article>
    </div>
  );
}
